import React, { useEffect, useState } from "react";
import { useAuthStore } from "../store/AuthStore";
import { useBirdRadarStore } from "../store/RadarStore";

interface SavedLocationsPanelProps {
  onClose: () => void;
}

export default function SavedLocationsPanel({ onClose }: SavedLocationsPanelProps) {
  const { user, savedLocations, isSavedLoading, fetchSavedLocations, saveLocation, deleteLocation, logout } = useAuthStore();
  const { currentLat, currentLng, setLocation } = useBirdRadarStore();
  const [labelInput, setLabelInput] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchSavedLocations();
  }, [fetchSavedLocations]);

  const handleSaveCurrent = async () => {
    if (!currentLat || !currentLng) return;
    const label = labelInput.trim() || `Location ${savedLocations.length + 1}`;
    setIsSaving(true);
    await saveLocation(label, currentLat, currentLng);
    setLabelInput("");
    setIsSaving(false);
  };

  const handleNavigate = (lat: number, lng: number) => {
    setLocation(lat, lng);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-neutral-900 border border-neutral-800 rounded-t-3xl md:rounded-3xl shadow-2xl w-full max-w-sm flex flex-col max-h-[80vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-neutral-800 flex-shrink-0">
          <div>
            <h2 className="text-white font-bold text-base">💾 Saved Locations</h2>
            <p className="text-neutral-500 text-xs mt-0.5">{user?.displayName || user?.email}</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={logout}
              className="text-[11px] text-neutral-500 hover:text-red-400 transition"
            >
              Sign out
            </button>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-neutral-800 hover:bg-neutral-700 text-white transition text-sm"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Save Current Location */}
        <div className="px-5 py-3 border-b border-neutral-800 flex-shrink-0">
          <p className="text-xs text-neutral-500 mb-2">Save current radar position:</p>
          <div className="flex gap-2">
            <input
              type="text"
              value={labelInput}
              onChange={(e) => setLabelInput(e.target.value)}
              placeholder="Give it a name..."
              className="flex-1 bg-neutral-800 border border-neutral-700 rounded-xl px-3 py-2 text-sm text-white placeholder-neutral-600 outline-none focus:border-blue-500 transition"
            />
            <button
              onClick={handleSaveCurrent}
              disabled={isSaving || !currentLat}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm font-semibold rounded-xl transition flex-shrink-0"
            >
              {isSaving ? "..." : "⭐ Save"}
            </button>
          </div>
        </div>

        {/* Locations List */}
        <div className="flex-1 overflow-y-auto px-5 py-3 flex flex-col gap-2">
          {isSavedLoading && (
            <div className="flex justify-center py-8">
              <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {!isSavedLoading && savedLocations.length === 0 && (
            <div className="text-center py-10 text-neutral-600">
              <div className="text-4xl mb-2">📍</div>
              <p className="text-sm">No saved locations yet.</p>
              <p className="text-xs mt-1">Navigate to a spot and save it above!</p>
            </div>
          )}

          {savedLocations.map((loc) => (
            <div
              key={loc.id}
              className="flex items-center gap-3 bg-neutral-800/60 hover:bg-neutral-800 rounded-2xl px-4 py-3 transition group"
            >
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{loc.label}</p>
                <p className="text-neutral-500 text-[11px] font-mono mt-0.5">
                  {loc.latitude.toFixed(4)}, {loc.longitude.toFixed(4)}
                </p>
              </div>
              <button
                onClick={() => handleNavigate(loc.latitude, loc.longitude)}
                title="Go to this location"
                className="text-blue-400 hover:text-blue-300 text-sm transition opacity-0 group-hover:opacity-100"
              >
                →
              </button>
              <button
                onClick={() => deleteLocation(loc.id)}
                title="Delete"
                className="text-neutral-600 hover:text-red-400 text-sm transition opacity-0 group-hover:opacity-100"
              >
                🗑
              </button>
            </div>
          ))}
        </div>

        {/* Footer padding */}
        <div className="h-3 flex-shrink-0" />
      </div>
    </div>
  );
}
